import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Demo Notification metadata schema
 */
export const DemoNotificationMetadataSchema = z
  .object({
    agreementId: z.string().optional(),
  })
  .catchall(z.unknown())
  .nullable();

/**
 * Demo Notification schema
 */
export const DemoNotificationSchema = z.object({
  id: z.uuid(),
  text: z.string(), // Notification key for frontend content mapping
  status: z.string(), // "unread" or "read"
  metadata: DemoNotificationMetadataSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * List Notifications Response Schema
 */
export const ListDemoNotificationsResponseSchema = z.object({
  notifications: z.array(DemoNotificationSchema),
});

/**
 * Mark As Read Input Schema
 */
export const MarkAsReadInputSchema = z.object({
  id: z.uuid(),
});

/**
 * Mark As Read Response Schema
 */
export const MarkAsReadResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * Reseed Response Schema
 */
export const ReseedResponseSchema = z.object({
  success: z.boolean(),
  count: z.number(),
});

/**
 * Seed Database Input Schema
 */
export const SeedDatabaseInputSchema = z.object({
  seedId: z.string().optional(),
});

/**
 * Seed Database Response Schema
 */
export const SeedDatabaseResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  results: z.array(
    z.object({
      name: z.string(),
      count: z.number(),
      message: z.string().optional(),
    }),
  ),
});

/**
 * Reset Database Response Schema
 */
export const ResetDatabaseResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  deletedCounts: z.record(z.string(), z.number()),
});

/**
 * Demo Notifications Contracts
 */
export const demoNotificationsContracts = {
  // List all notifications for current organization
  list: oc.output(ListDemoNotificationsResponseSchema).route({
    method: "GET",
  }),

  // Mark a notification as read
  markAsRead: oc
    .input(MarkAsReadInputSchema)
    .output(MarkAsReadResponseSchema)
    .route({
      method: "POST",
    }),

  // Delete all and reseed demo notifications
  reseed: oc.output(ReseedResponseSchema).route({
    method: "POST",
  }),
} as const;

/**
 * Demo Database Seeding Contracts
 */
export const demoSeedingContracts = {
  // Seed the database with demo data
  seed: oc
    .input(SeedDatabaseInputSchema)
    .output(SeedDatabaseResponseSchema)
    .route({
      method: "POST",
    }),

  // Reset (delete) all demo data
  reset: oc.output(ResetDatabaseResponseSchema).route({
    method: "POST",
  }),
} as const;

// Export types
export type DemoNotification = z.infer<typeof DemoNotificationSchema>;
export type ListDemoNotificationsResponse = z.infer<
  typeof ListDemoNotificationsResponseSchema
>;
export type MarkAsReadInput = z.infer<typeof MarkAsReadInputSchema>;
export type MarkAsReadResponse = z.infer<typeof MarkAsReadResponseSchema>;
export type SeedDatabaseInput = z.infer<typeof SeedDatabaseInputSchema>;
export type SeedDatabaseResponse = z.infer<typeof SeedDatabaseResponseSchema>;
export type ResetDatabaseResponse = z.infer<typeof ResetDatabaseResponseSchema>;
